import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import "./agenda.css";
import { Icon } from "@iconify/react/dist/iconify.js";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import useAgenda from "../../hooks/useAgenda";
import AgendaEventModal from "./AgendaEventModal";
import AgendaEventDetailModal from "./AgendaEventDetailModal";
import AgendaDeleteConfirmModal from "./AgendaDeleteConfirmModal";

/* ─── Constantes ─── */
const TIPO_COLORS = {
  VISITA: { bg: "#e0f2fe", border: "#0284c7", text: "#0c4a6e" },
  REUNION: { bg: "#fef3c7", border: "#d97706", text: "#78350f" },
  TAREA: { bg: "#dcfce7", border: "#16a34a", text: "#14532d" },
  OTRO: { bg: "#f3e8ff", border: "#9333ea", text: "#581c87" },
};

const TIPO_LABELS = {
  VISITA: "Visita",
  REUNION: "Reunión",
  TAREA: "Tarea",
  OTRO: "Otro",
};

const ESTADO_BADGES = {
  PENDIENTE: { class: "bg-warning-focus text-warning-main", label: "Pendiente" },
  COMPLETADA: { class: "bg-success-focus text-success-main", label: "Completada" },
  CANCELADA: { class: "bg-danger-focus text-danger-main", label: "Cancelada" },
};

const AgendaLayer = () => {
  const calendarRef = useRef(null);

  const {
    events,
    loading,
    selectedMonth,
    estudiantes,
    loadingEstudiantes,
    canAssignStudent,
    setSelectedMonth,
    setSearch,
    goToPrevMonth,
    goToNextMonth,
    goToToday,
    createEvent,
    updateEvent,
    patchEvent,
    deleteEvent,
  } = useAgenda();

  // ─── Estado de modales ───
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [initialDate, setInitialDate] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [viewMode, setViewMode] = useState("calendar"); // "calendar" | "list"
  const [submitting, setSubmitting] = useState(false);

  // ─── Mapear eventos al formato FullCalendar ───
  const calendarEvents = useMemo(() => {
    return events.map((evt) => {
      const colors = TIPO_COLORS[evt.tipo] || TIPO_COLORS.OTRO;
      return {
        id: String(evt.id),
        title: evt.titulo,
        start: evt.fecha_hora_inicio,
        end: evt.fecha_hora_fin || undefined,
        backgroundColor: colors.bg,
        borderColor: colors.border,
        textColor: colors.text,
        extendedProps: { ...evt },
      };
    });
  }, [events]);

  // ─── Sincronizar mes del calendario con el estado ───
  useEffect(() => {
    if (calendarRef.current) {
      const calApi = calendarRef.current.getApi();
      const [year, month] = selectedMonth.split("-").map(Number);
      calApi.gotoDate(new Date(year, month - 1, 1));
    }
  }, [selectedMonth]);

  // ─── Helpers del mes ───
  const monthLabel = useMemo(() => {
    const [y, m] = selectedMonth.split("-").map(Number);
    const d = new Date(y, m - 1, 1);
    return d.toLocaleDateString("es-MX", { month: "long", year: "numeric" });
  }, [selectedMonth]);

  // ─── Handlers del calendario ───
  const handleDateClick = useCallback((info) => {
    setInitialDate(info.dateStr);
    setSelectedEvent(null);
    setShowCreateModal(true);
  }, []);

  const handleEventClick = useCallback((info) => {
    const evt = info.event.extendedProps;
    setSelectedEvent(evt);
    setShowDetailModal(true);
  }, []);

  const handleCalendarDatesSet = useCallback(
    (dateInfo) => {
      const start = dateInfo.view.currentStart;
      const newMonth = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`;
      if (newMonth !== selectedMonth) {
        setSelectedMonth(newMonth);
      }
    },
    [selectedMonth, setSelectedMonth],
  );

  // ─── CRUD Handlers ───
  const handleCreateSubmit = async (payload) => {
    setSubmitting(true);
    try {
      await createEvent(payload);
      setShowCreateModal(false);
    } catch {
      // El toast de error ya se muestra en useAgenda
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (payload) => {
    if (!selectedEvent) return;
    setSubmitting(true);
    try {
      await updateEvent(selectedEvent.id, payload);
      setShowEditModal(false);
      setSelectedEvent(null);
    } catch {
      // El toast de error ya se muestra en useAgenda
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (eventId, newStatus) => {
    try {
      await patchEvent(eventId, { estado: newStatus });
      setShowDetailModal(false);
      setSelectedEvent(null);
    } catch {
      // El toast de error ya se muestra en useAgenda
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedEvent) return;
    setSubmitting(true);
    try {
      await deleteEvent(selectedEvent.id);
      setShowDeleteModal(false);
      setShowDetailModal(false);
      setSelectedEvent(null);
    } catch {
      // El toast de error ya se muestra en useAgenda
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const openEditFromDetail = () => {
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  const openDeleteFromDetail = () => {
    setShowDetailModal(false);
    setShowDeleteModal(true);
  };

  // ─── Contadores rápidos ───
  const todayEvents = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    return events.filter(
      (e) => e.fecha_hora_inicio && e.fecha_hora_inicio.startsWith(today),
    );
  }, [events]);

  const pendingCount = events.filter((e) => e.estado === "PENDIENTE").length;
  const completedCount = events.filter((e) => e.estado === "COMPLETADA").length;

  return (
    <>
      {/* ─── Header con estadísticas ─── */}
      <div className="row gy-4 mb-24">
        <div className="col-xxl-3 col-sm-6">
          <div className="card p-0 shadow-none radius-12 overflow-hidden border agenda-stat-card">
            <div className="card-body p-20 d-flex align-items-center gap-16">
              <div className="w-48-px h-48-px bg-primary-100 d-flex justify-content-center align-items-center radius-12 flex-shrink-0">
                <Icon icon="solar:calendar-bold" className="text-primary-600 text-2xl" />
              </div>
              <div>
                <span className="text-secondary-light text-sm fw-medium">
                  Total del mes
                </span>
                <h5 className="fw-semibold mb-0">{events.length}</h5>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xxl-3 col-sm-6">
          <div className="card p-0 shadow-none radius-12 overflow-hidden border agenda-stat-card">
            <div className="card-body p-20 d-flex align-items-center gap-16">
              <div className="w-48-px h-48-px bg-info-100 d-flex justify-content-center align-items-center radius-12 flex-shrink-0">
                <Icon icon="solar:clock-circle-bold" className="text-info-600 text-2xl" />
              </div>
              <div>
                <span className="text-secondary-light text-sm fw-medium">Hoy</span>
                <h5 className="fw-semibold mb-0">{todayEvents.length}</h5>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xxl-3 col-sm-6">
          <div className="card p-0 shadow-none radius-12 overflow-hidden border agenda-stat-card">
            <div className="card-body p-20 d-flex align-items-center gap-16">
              <div className="w-48-px h-48-px bg-warning-100 d-flex justify-content-center align-items-center radius-12 flex-shrink-0">
                <Icon icon="solar:hourglass-bold" className="text-warning-600 text-2xl" />
              </div>
              <div>
                <span className="text-secondary-light text-sm fw-medium">Pendientes</span>
                <h5 className="fw-semibold mb-0">{pendingCount}</h5>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xxl-3 col-sm-6">
          <div className="card p-0 shadow-none radius-12 overflow-hidden border agenda-stat-card">
            <div className="card-body p-20 d-flex align-items-center gap-16">
              <div className="w-48-px h-48-px bg-success-100 d-flex justify-content-center align-items-center radius-12 flex-shrink-0">
                <Icon icon="solar:check-circle-bold" className="text-success-600 text-2xl" />
              </div>
              <div>
                <span className="text-secondary-light text-sm fw-medium">
                  Completadas
                </span>
                <h5 className="fw-semibold mb-0">{completedCount}</h5>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Toolbar ─── */}
      <div className="card radius-12 mb-24">
        <div className="card-body p-20">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-16">
            {/* Izquierda: navegación del mes */}
            <div className="d-flex align-items-center gap-12">
              <button
                className="btn btn-outline-primary btn-sm radius-8 d-flex align-items-center gap-1"
                onClick={goToToday}
              >
                <Icon icon="solar:calendar-minimalistic-bold" className="text-lg" />
                Hoy
              </button>
              <div className="d-flex align-items-center gap-4">
                <button
                  className="btn btn-outline-secondary btn-sm radius-8 w-32-px h-32-px d-flex align-items-center justify-content-center p-0"
                  onClick={goToPrevMonth}
                >
                  <Icon icon="mdi:chevron-left" className="text-xl" />
                </button>
                <h6 className="fw-semibold mb-0 text-capitalize px-8 text-nowrap">
                  {monthLabel}
                </h6>
                <button
                  className="btn btn-outline-secondary btn-sm radius-8 w-32-px h-32-px d-flex align-items-center justify-content-center p-0"
                  onClick={goToNextMonth}
                >
                  <Icon icon="mdi:chevron-right" className="text-xl" />
                </button>
              </div>
            </div>

            {/* Centro: búsqueda */}
            <form
              className="d-flex align-items-center gap-8"
              onSubmit={handleSearchSubmit}
            >
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control form-control-sm radius-8 ps-40"
                  placeholder="Buscar eventos..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ minWidth: "220px" }}
                />
                <Icon
                  icon="mdi:magnify"
                  className="position-absolute top-50 translate-middle-y ms-12 text-secondary-light text-lg"
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary btn-sm radius-8 d-flex align-items-center gap-1"
              >
                <Icon icon="mdi:magnify" className="text-lg" />
              </button>
            </form>

            {/* Derecha: toggle vista + botón crear */}
            <div className="d-flex align-items-center gap-8">
              <div className="btn-group" role="group">
                <button
                  className={`btn btn-sm radius-8 ${viewMode === "calendar" ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => setViewMode("calendar")}
                >
                  <Icon icon="solar:calendar-bold" className="text-lg" />
                </button>
                <button
                  className={`btn btn-sm radius-8 ${viewMode === "list" ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => setViewMode("list")}
                >
                  <Icon icon="solar:list-bold" className="text-lg" />
                </button>
              </div>
              <button
                className="btn btn-primary btn-sm radius-8 d-flex align-items-center gap-6"
                onClick={() => {
                  setInitialDate(null);
                  setSelectedEvent(null);
                  setShowCreateModal(true);
                }}
              >
                <Icon icon="mdi:plus" className="text-lg" />
                Nuevo Evento
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Vista Calendario ─── */}
      {viewMode === "calendar" && (
        <div className="card radius-12">
          <div className="card-body p-24 agenda-calendar-wrapper position-relative">
            {/* Loading overlay – se muestra encima sin desmontar el calendario */}
            {loading && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
                style={{
                  backgroundColor: "rgba(255,255,255,0.75)",
                  zIndex: 10,
                  borderRadius: "inherit",
                }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="text-secondary-light mt-12">Cargando agenda...</p>
              </div>
            )}
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              locale="es"
              headerToolbar={false}
              events={calendarEvents}
              dateClick={handleDateClick}
              eventClick={handleEventClick}
              datesSet={handleCalendarDatesSet}
              editable={false}
              selectable={true}
              dayMaxEvents={3}
              height="auto"
              buttonText={{
                today: "Hoy",
                month: "Mes",
                week: "Semana",
                day: "Día",
              }}
              eventDisplay="block"
              eventTimeFormat={{
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }}
            />
          </div>
        </div>
      )}

      {/* ─── Vista Lista ─── */}
      {viewMode === "list" && (
        <div className="card radius-12">
          <div className="card-body p-0 position-relative">
            {/* Loading overlay para la vista lista */}
            {loading && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
                style={{
                  backgroundColor: "rgba(255,255,255,0.75)",
                  zIndex: 10,
                  minHeight: "200px",
                }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="text-secondary-light mt-12">Cargando agenda...</p>
              </div>
            )}
            {events.length === 0 ? (
              <div className="text-center py-60">
                <Icon
                  icon="solar:calendar-search-bold-duotone"
                  className="text-secondary-light mb-16"
                  style={{ fontSize: "64px" }}
                />
                <h6 className="text-secondary-light fw-medium">
                  No hay eventos para este mes
                </h6>
                <p className="text-secondary-light text-sm">
                  Haz clic en "Nuevo Evento" para agregar uno.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th className="text-sm fw-semibold text-secondary-light ps-24 py-16">
                        Título
                      </th>
                      <th className="text-sm fw-semibold text-secondary-light py-16">
                        Tipo
                      </th>
                      <th className="text-sm fw-semibold text-secondary-light py-16">
                        Estado
                      </th>
                      <th className="text-sm fw-semibold text-secondary-light py-16">
                        Fecha Inicio
                      </th>
                      <th className="text-sm fw-semibold text-secondary-light py-16">
                        Creado por
                      </th>
                      <th className="text-sm fw-semibold text-secondary-light py-16">
                        Asignado a
                      </th>
                      <th className="text-sm fw-semibold text-secondary-light py-16">
                        Lugar
                      </th>
                      <th className="text-sm fw-semibold text-secondary-light pe-24 py-16 text-center">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((evt) => {
                      const estadoBadge =
                        ESTADO_BADGES[evt.estado] || ESTADO_BADGES.PENDIENTE;
                      return (
                        <tr key={evt.id} className="agenda-list-row">
                          <td className="ps-24 py-16">
                            <div className="d-flex align-items-center gap-10">
                              <span
                                className="w-10-px h-10-px rounded-circle flex-shrink-0"
                                style={{
                                  backgroundColor:
                                    (TIPO_COLORS[evt.tipo] || TIPO_COLORS.OTRO).border,
                                }}
                              />
                              <span className="fw-semibold text-primary-light text-sm">
                                {evt.titulo}
                              </span>
                            </div>
                          </td>
                          <td className="py-16">
                            <span className="text-sm text-secondary-light">
                              {TIPO_LABELS[evt.tipo] || evt.tipo}
                            </span>
                          </td>
                          <td className="py-16">
                            <span
                              className={`px-12 py-4 radius-4 fw-medium text-xs ${estadoBadge.class}`}
                            >
                              {estadoBadge.label}
                            </span>
                          </td>
                          <td className="py-16">
                            <span className="text-sm text-secondary-light">
                              {formatDateTime(evt.fecha_hora_inicio)}
                            </span>
                          </td>
                          <td className="py-16">
                            <span className="text-sm text-secondary-light">
                              {evt.creado_por_nombre || "—"}
                            </span>
                          </td>
                          <td className="py-16">
                            <span className="text-sm text-secondary-light">
                              {evt.asignado_a_nombre || "—"}
                            </span>
                          </td>
                          <td className="py-16">
                            <span className="text-sm text-secondary-light">
                              {evt.lugar || "—"}
                            </span>
                          </td>
                          <td className="pe-24 py-16 text-center">
                            <div className="d-flex align-items-center justify-content-center gap-8">
                              <button
                                className="btn btn-sm btn-outline-info radius-8 w-32-px h-32-px d-flex align-items-center justify-content-center p-0"
                                title="Ver detalle"
                                onClick={() => {
                                  setSelectedEvent(evt);
                                  setShowDetailModal(true);
                                }}
                              >
                                <Icon icon="mdi:eye-outline" className="text-lg" />
                              </button>
                              {evt.puede_modificar && (
                                <>
                                  <button
                                    className="btn btn-sm btn-outline-warning radius-8 w-32-px h-32-px d-flex align-items-center justify-content-center p-0"
                                    title="Editar"
                                    onClick={() => {
                                      setSelectedEvent(evt);
                                      setShowEditModal(true);
                                    }}
                                  >
                                    <Icon icon="mdi:pencil-outline" className="text-lg" />
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger radius-8 w-32-px h-32-px d-flex align-items-center justify-content-center p-0"
                                    title="Eliminar"
                                    onClick={() => {
                                      setSelectedEvent(evt);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    <Icon icon="mdi:trash-can-outline" className="text-lg" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Leyenda de tipos ─── */}
      {(
        <div className="d-flex flex-wrap align-items-center gap-20 mt-16">
          {Object.entries(TIPO_LABELS).map(([key, label]) => (
            <div key={key} className="d-flex align-items-center gap-6">
              <span
                className="w-12-px h-12-px rounded-circle"
                style={{ backgroundColor: TIPO_COLORS[key].border }}
              />
              <span className="text-sm text-secondary-light">{label}</span>
            </div>
          ))}
        </div>
      )}

      {/* ─── Modales ─── */}
      <AgendaEventModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSubmit={handleCreateSubmit}
        submitting={submitting}
        canAssignStudent={canAssignStudent}
        estudiantes={estudiantes}
        loadingEstudiantes={loadingEstudiantes}
        initialDate={initialDate}
        title="Nuevo Evento"
      />

      <AgendaEventModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedEvent(null);
        }}
        onSubmit={handleEditSubmit}
        submitting={submitting}
        canAssignStudent={canAssignStudent}
        estudiantes={estudiantes}
        loadingEstudiantes={loadingEstudiantes}
        eventData={selectedEvent}
        title="Editar Evento"
      />

      <AgendaEventDetailModal
        show={showDetailModal}
        onHide={() => {
          setShowDetailModal(false);
          setSelectedEvent(null);
        }}
        event={selectedEvent}
        onEdit={openEditFromDetail}
        onDelete={openDeleteFromDetail}
        onStatusChange={handleStatusChange}
      />

      <AgendaDeleteConfirmModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedEvent(null);
        }}
        onConfirm={handleDeleteConfirm}
        submitting={submitting}
        eventTitle={selectedEvent?.titulo}
      />
    </>
  );
};

/* ─── Utilidades ─── */
function formatDateTime(isoString) {
  if (!isoString) return "—";
  const d = new Date(isoString);
  return d.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export default AgendaLayer;
